import {makeAutoObservable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {ConferenceExpandedCommon} from '../../../api/conference';
import type {WithBusyManager} from '../../../utils/store';

import {alert} from '../../../components/alert';
import {conferenceStartRecord} from '../../../services/entity-action/conference/conference.start-record';
import {conferenceStopRecord} from '../../../services/entity-action/conference/conference.stop-record';
import {
  isConferenceCorporate,
  isMeConferenceHost,
} from '../../../utils/entity/conference';
import {envManager} from '../../../utils/env';
import {AppError} from '../../../utils/errors/app';
import {logger} from '../../../utils/logger';
import {AsyncBusyManager} from '../../../utils/store';

type ReasonKey = 'startRecord' | 'stopRecord';

export class RecordingManager implements WithBusyManager<ReasonKey> {
  readonly busy = new AsyncBusyManager<ReasonKey>();
  conference: ConferenceExpandedCommon | null = null;
  isRecording = false;

  constructor() {
    this.busy.observe<RecordingManager, 'startRecord' | 'stopRecord'>(this, {
      startRecord: 'startRecord',
      stopRecord: 'stopRecord',
    });

    makeAutoObservable(this, {}, {autoBind: true});
  }

  get actionLabel() {
    return this.isRecording
      ? i18n.t('shared:Conference.stop_record_label')
      : i18n.t('shared:Conference.start_record_label');
  }

  get recordIsAvailable() {
    // in b2b env the backend starts recording automatically
    // when a client join. If the conference is corporate on core
    // disable record button as well as we will start it automatically on client join
    if (isConferenceCorporate(this.conference)) {
      return false;
    }

    return !!(this.conference && isMeConferenceHost(this.conference));
  }

  get shouldShowRecordingLabel() {
    const isCorporate = isConferenceCorporate(this.conference);
    const isCorporateWithoutCourse = isCorporate && !this.conference?.course_id;

    if (envManager.isB2b || isCorporateWithoutCourse) {
      return false;
    }

    return this.isRecording;
  }

  setConference(conference: ConferenceExpandedCommon) {
    this.conference = conference;
  }

  setIsRecording(isRecording: boolean) {
    this.isRecording = isRecording;
  }

  async startRecord() {
    if (this.isRecording) {
      return;
    }

    try {
      // TODO: maybe we should move this check to the top? or AppError should be alerted for some reason?
      if (!this.conference) {
        throw new AppError('conference is not defined');
      }

      const conference = await conferenceStartRecord.asCoach({
        entity: this.conference,
        isCorporate: isConferenceCorporate(this.conference),
        params: {},
      });

      this.setIsRecording(Boolean(conference?.is_recording));
    } catch (error) {
      logger.error(error);

      await alert.error(error);
    }
  }

  async stopRecord() {
    if (!this.isRecording) {
      return;
    }

    try {
      // TODO: maybe we should move this check to the top? or AppError should be alerted for some reason?
      if (!this.conference) {
        throw new AppError('conference is not defined');
      }

      await conferenceStopRecord.asCoach({
        entity: this.conference,
        params: {},
      });

      this.setIsRecording(false);
    } catch (error) {
      logger.error(error);

      await alert.error(error);
    }
  }

  toggleRecord() {
    if (this.isRecording) {
      this.stopRecord();
    } else {
      this.startRecord();
    }
  }
}
